/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonSpinner, IonToast, useIonToast } from '@ionic/react';
import { FullBasicPost, Post, objectEquals } from 'model';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useDeletePostMutation, useGetPostQuery, useUpdatePostMutation } from '../api/PostApi';
import FormFooter from '../components/FormFooter';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { PostEditFields } from '../components/PostEditFields';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { PostEditRules } from '../formConfigs/EditPostFieldsConfig';
import { useFormValues } from '../hooks/useFormValues';

export const EditPost: React.FC = () => {
	const { postid } = useParams<{ postid: Post['_id'] }>();
	const { data: fetchPost } = useGetPostQuery(postid, { skip: !postid });
	const [post, setPost] = useState<FullBasicPost | undefined | null>(fetchPost as FullBasicPost);
	const history = useHistory();
	const [deleteItem] = useDeletePostMutation();
	const [updatePost, { isError: updateIsError, isLoading: updateIsLoading, error: updateError }] =
		useUpdatePostMutation();

	const {
		setValues,
		formSuccess,
		formValues,
		validateForm,
		stateDataObject: newPost
	} = useFormValues<FullBasicPost>(PostEditRules, post, objectEquals);

	const [showToast] = useIonToast();
	const onSubmit = async () => {
		const compiledNoErrors = validateForm();
		if (compiledNoErrors && formValues && newPost) {
			if (post) {
				const result = await updatePost({
					post: {
						...post,
						...newPost,
						draft: false
					}
				});
				if (!('error' in result)) {
					setPost(result.data as FullBasicPost);
					showToast({
						color: 'success',
						message: 'Post has been updated',
						position: 'top',
						duration: 1500
					});
					history.push('/main/latest');
				} else {
					showToast({
						color: 'danger',
						message: 'Error updating post',
						position: 'top',
						duration: 1500
					});
				}
			}
		}
	};
	const onCancel = async () => {
		history.go(-1);

		if (fetchPost && fetchPost.draft) {
			await deleteItem(fetchPost._id);
		}
	};

	useEffect(() => {
		if (fetchPost) {
			setPost(fetchPost as FullBasicPost);
		}
		return () => setPost(undefined);
	}, [setPost, fetchPost]);

	return (
		<QuirkIonPage name="Edit Post" data={{ poll: postid }}>
			<Header
				title="Edit Post"
				left={<IconButton icon={<IconClose />} onClick={onCancel} />}
				right={
					<IconButton
						fill="solid"
						label="Done"
						disabled={!formSuccess}
						icon={<IconCheck />}
						onClick={onSubmit}
						testId="editPostDoneButton"
					/>
				}
			/>

			<QuirkIonContent flush padding>
				<>
					{post && formValues && (
						<PostEditFields setValues={setValues} setPost={setPost} formValues={formValues} post={post} />
					)}
					{post && formValues && (
						<FormFooter onCancel={onCancel} onSubmit={onSubmit} disableSubmit={!formSuccess} />
					)}
					<IonSpinner name="crescent" hidden={!updateIsLoading} />
					<IonToast
						isOpen={updateIsError}
						duration={1500}
						message={(updateError as any)?.data}
						position="top"
						color="danger"
					/>
				</>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
